import Card, { CardHeader, CardBody } from '../ui/Card';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { bebauungsplaeneApi } from '../../api/bebauungsplaene';
import { GEBIETSART_LABELS } from '../../types/bebauungsplan';
import { useIsModuleEnabled } from '../../hooks/useConfigVisibility';

interface Props {
  latitude: number | null;
  longitude: number | null;
}

export default function BebauungsplanInfoCard({ latitude, longitude }: Props) {
  // Hooks must run unconditionally; the visibility gate is applied below.
  const bplanVisible = useIsModuleEnabled('bebauungsplaene');
  const hasCoords = latitude != null && longitude != null;

  const { data, isLoading } = useQuery({
    queryKey: ['bebauungsplan-match', latitude, longitude],
    queryFn: () => bebauungsplaeneApi.matchAtCoords(latitude as number, longitude as number),
    enabled: hasCoords,
  });

  // Status badge wie bei VermieterPicker / anderen Sektionen
  let status: { label: string; color: string; dot: string };
  if (!hasCoords) {
    status = { label: 'Nicht geo-kodiert', color: 'bg-gray-100 text-gray-600', dot: 'bg-gray-400' };
  } else if (isLoading) {
    status = { label: 'Wird geprüft…', color: 'bg-gray-100 text-gray-600', dot: 'bg-gray-400' };
  } else if (data && data.is_reines_wohngebiet) {
    status = { label: 'Reines Wohngebiet', color: 'bg-red-100 text-red-800', dot: 'bg-red-500' };
  } else if (data && data.matches.length > 0) {
    status = { label: 'Zugeordnet', color: 'bg-blue-100 text-blue-800', dot: 'bg-blue-500' };
  } else {
    status = { label: 'Kein B-Plan', color: 'bg-gray-100 text-gray-600', dot: 'bg-gray-400' };
  }

  if (!bplanVisible) return null;

  return (
    <Card>
      <CardHeader className="flex items-center justify-between">
        <div className="flex items-center gap-3 flex-wrap">
          <h3 className="text-lg font-medium">Bebauungsplan</h3>
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium ${status.color}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${status.dot}`} />
            {status.label}
          </span>
        </div>
        <Link to="/bebauungsplaene" className="text-sm text-blue-600 hover:text-blue-800 shrink-0">
          Alle B-Pläne
        </Link>
      </CardHeader>

      <CardBody>
        {!hasCoords && (
          <p className="text-sm text-gray-500">
            Keine Koordinaten hinterlegt — kein B-Plan-Abgleich möglich.
          </p>
        )}

        {hasCoords && isLoading && (
          <p className="text-sm text-gray-400">Lade…</p>
        )}

        {hasCoords && data && data.matches.length === 0 && (
          <p className="text-sm text-gray-500">
            Keinem hinterlegten Bebauungsplan zugeordnet.
          </p>
        )}

        {hasCoords && data && data.matches.length > 0 && (
          <div className="space-y-2">
            {data.matches.map((m) => {
              const isWR = m.gebietsart === 'WR';
              return (
                <Link
                  key={m.id}
                  to={`/bebauungsplaene/${m.id}`}
                  className={`block border rounded-lg px-3 py-2 hover:bg-gray-50 transition-colors ${
                    isWR ? 'border-red-200' : 'border-gray-200'
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-gray-900 truncate">{m.name}</div>
                      {m.plan_nr && (
                        <div className="text-xs text-gray-500">Plan-Nr.: {m.plan_nr}</div>
                      )}
                    </div>
                    <span
                      className={`shrink-0 inline-flex px-2 py-0.5 rounded-full text-xs font-medium ring-1 ${
                        isWR
                          ? 'bg-red-50 text-red-700 ring-red-200'
                          : 'bg-gray-100 text-gray-700 ring-gray-200'
                      }`}
                    >
                      {GEBIETSART_LABELS[m.gebietsart] ?? m.gebietsart}
                    </span>
                  </div>
                  {m.rechtskraft_datum && (
                    <div className="text-xs text-gray-400 mt-1">
                      Rechtskräftig seit {m.rechtskraft_datum}
                    </div>
                  )}
                </Link>
              );
            })}
          </div>
        )}
      </CardBody>
    </Card>
  );
}
